import React from 'react';
import { useTranslation } from 'react-i18next';
import { SentimentBadge } from '@/components/SentimentBadge';
import { TileModal } from '@/components/Dashboard/TileModal';
import { UnreadDot } from '@/components/Dashboard/UnreadDot';
import { useTeacherMessages } from '@/components/Dashboard/hooks/useTeacherMessages';
import {
  formatMonth,
  formatShortDate,
  groupByMonth,
  sortByDateDesc,
} from '@/utils/date';
import { linkifyText } from '@/utils/html';
import { BULLET_SYMBOL } from '@/utils/text';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const DiaryModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { data } = useTeacherMessages();
  const sorted = sortByDateDesc(data ?? [], ({ date }) => date);
  const groups = Array.from(groupByMonth(sorted, ({ date }) => date).values());

  return (
    <TileModal
      isOpen={isOpen}
      title={t('home.dashboard.diary.title')}
      onClose={onClose}
    >
      <div className="flex flex-col gap-6 px-4 py-4 md:px-6 md:py-6">
        {groups.map((items, monthIndex) => (
          <section key={monthIndex} className="flex flex-col gap-3">
            <h3 className="text-headline-20 capitalize text-foreground">
              {formatMonth(items[0].date)}
            </h3>
            <ul className="bg-card shadow-card flex flex-col rounded-xl">
              {items.map((message) => (
                <li
                  key={message.id}
                  className="flex flex-col gap-2 border-b border-border p-4 last:border-b-0 md:min-h-[80px] md:flex-row md:items-center md:gap-4 md:px-5"
                >
                  <div className="flex items-center justify-between gap-3 md:hidden">
                    <span className="text-caption-12 bg-on-primary-surface min-w-0 truncate rounded-xl px-2 py-0.5 tracking-[0.025em] text-foreground">
                      {message.typeName}
                    </span>
                    <span className="text-body-13 flex shrink-0 items-center gap-1 text-subtle-foreground md:text-body-14">
                      {formatShortDate(message.date)}
                      {message.isNew && <UnreadDot />}
                    </span>
                  </div>
                  <div className="hidden w-1/3 min-w-0 flex-col gap-1 md:flex">
                    <div className="flex items-center gap-1">
                      {message.isNew && <UnreadDot />}
                      <span className="text-body-14 text-muted-foreground">
                        {formatShortDate(message.date)}
                      </span>
                      <span className="text-caption-12 bg-on-primary-surface truncate rounded-xl px-2 py-0.5 tracking-[0.025em] text-foreground">
                        {message.typeName}
                      </span>
                    </div>
                    <div className="text-subhead-14 flex items-center gap-1 truncate text-foreground">
                      <span>{message.authorName}</span>
                      {message.behaviorEntry && (
                        <>
                          <span className="text-subtle-foreground">
                            {BULLET_SYMBOL}
                          </span>
                          <span>({message.behaviorEntry.disciplineName})</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 md:hidden">
                    <div className="text-subhead-14 flex min-w-0 flex-1 items-center gap-1 truncate text-foreground">
                      <span>{message.authorName}</span>
                      {message.behaviorEntry && (
                        <>
                          <span className="text-subtle-foreground">
                            {BULLET_SYMBOL}
                          </span>
                          <span>({message.behaviorEntry.disciplineName})</span>
                        </>
                      )}
                    </div>
                    {message.behaviorEntry && (
                      <SentimentBadge type={message.behaviorEntry.type} />
                    )}
                  </div>
                  <p
                    className="rich-text text-body-13 min-w-0 whitespace-pre-line break-words text-muted-foreground md:text-body-14 md:flex-1"
                    dangerouslySetInnerHTML={{
                      __html: linkifyText(message.text),
                    }}
                  />
                  {message.behaviorEntry && (
                    <SentimentBadge
                      type={message.behaviorEntry.type}
                      className="hidden md:inline-flex"
                    />
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </TileModal>
  );
};
